import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, X } from "lucide-react";

/**
 * DateRangeFilter — preset chips (Today / 7d / 30d / This month / This quarter)
 * + custom range picker. Emits ISO date strings on change.
 *
 *   <DateRangeFilter value={{from, to, preset}} onChange={setRange} />
 *
 * `value.preset` is the chip key when a preset is active, otherwise null.
 */

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7d" },
  { key: "30d", label: "Last 30d" },
  { key: "month", label: "This month" },
  { key: "quarter", label: "This quarter" },
];

function isoDate(d) { return d.toISOString().slice(0, 10); }

function rangeForPreset(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = isoDate(now);
  let from;
  if (key === "today") from = isoDate(today);
  else if (key === "7d") { const d = new Date(today); d.setDate(d.getDate() - 6); from = isoDate(d); }
  else if (key === "30d") { const d = new Date(today); d.setDate(d.getDate() - 29); from = isoDate(d); }
  else if (key === "month") from = isoDate(new Date(now.getFullYear(), now.getMonth(), 1));
  else if (key === "quarter") { const q = Math.floor(now.getMonth() / 3); from = isoDate(new Date(now.getFullYear(), q * 3, 1)); }
  return { from, to };
}

export default function DateRangeFilter({ value, onChange, testId = "date-range" }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value?.from || "");
  const [customTo, setCustomTo] = useState(value?.to || "");

  const active = value?.preset;
  const hasRange = value?.from || value?.to;

  const pickPreset = (key) => {
    const r = rangeForPreset(key);
    onChange({ ...r, preset: key });
  };
  const clear = () => onChange({ from: null, to: null, preset: null });
  const applyCustom = () => {
    onChange({ from: customFrom || null, to: customTo || null, preset: null });
    setCustomOpen(false);
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testId}>
      {PRESETS.map((p) => (
        <button
          key={p.key}
          onClick={() => pickPreset(p.key)}
          data-testid={`${testId}-preset-${p.key}`}
          className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${active === p.key ? "bg-primary/20 border-primary/50 text-primary" : "bg-secondary/40 border-border text-muted-foreground hover:text-white hover:border-primary/40"}`}
        >
          {p.label}
        </button>
      ))}
      <Popover open={customOpen} onOpenChange={setCustomOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={`${testId}-custom-btn`}
            className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors flex items-center gap-1 ${(!active && hasRange) ? "bg-primary/20 border-primary/50 text-primary" : "bg-secondary/40 border-border text-muted-foreground hover:text-white hover:border-primary/40"}`}
          >
            <CalendarDays className="h-3 w-3" />
            {(!active && hasRange) ? `${value.from || "…"} → ${value.to || "…"}` : "Custom"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 rounded-sm bg-card border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Custom range</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground">From</label>
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="rounded-sm bg-secondary/40 h-8" data-testid={`${testId}-from`} />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">To</label>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="rounded-sm bg-secondary/40 h-8" data-testid={`${testId}-to`} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button size="sm" className="rounded-sm bg-primary" onClick={applyCustom} data-testid={`${testId}-apply`}>Apply</Button>
          </div>
        </PopoverContent>
      </Popover>
      {hasRange && (
        <button onClick={clear} data-testid={`${testId}-clear`} className="text-[11px] px-2 py-1 rounded-sm text-muted-foreground hover:text-red-400 transition-colors">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
